import React, { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Dropdown } from 'antd'
import { Icon } from '@iconify/react'

import {
    taskDeleteSupport,
    mainTaskDelete,
} from '../../taskRefactored/tasks.slice'
import { isTaskMutationSuccess } from '../../taskRefactored/tasks.service'
import { showActionNotification } from '../../../components/notification'
import { PRIVATE_ROUTES, buildProjectTaskTabSearch } from '../../../routes'
import { confirmDeleteTask, getDeleteTaskMenuLabel } from './confirmDeleteTask'
import { useTaskListKindPermission } from '../projectPermissions'

import styles from '../projectDetail.module.css'

const copyTaskContextLink = async (text) => {
    try {
        if (!text) return false
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return true
        }
    } catch {
        // fall through
    }
    try {
        const el = document.createElement('textarea')
        el.value = String(text)
        el.setAttribute('readonly', '')
        el.style.position = 'fixed'
        el.style.top = '-1000px'
        el.style.left = '-1000px'
        document.body.appendChild(el)
        el.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(el)
        return ok
    } catch {
        return false
    }
}

const TaskContextMenu = ({
    task,
    projectId,
    taskListKind = 'support',
    taskRouteView = 'list',
    onTaskClick,
    refreshTasks,
    readOnlyActions: readOnlyActionsProp = false,
    isTaskCompleted = false,
    isTogglingComplete = false,
    onToggleComplete,
    children,
}) => {
    const dispatch = useDispatch()
    const isMainCard = taskListKind === 'main'
    const { canEdit: canEditModule, canDelete: canDeleteModule } =
        useTaskListKindPermission(taskListKind)
    const readOnlyActions = readOnlyActionsProp || !canEditModule

    const taskDeepLink = useMemo(() => {
        if (!projectId || !task?._id) return ''
        const path = PRIVATE_ROUTES.projects.projectIdViewTask(
            projectId,
            taskRouteView,
            task._id
        )
        const search =
            taskListKind === 'main'
                ? buildProjectTaskTabSearch('development')
                : ''
        return `${window.location.origin}${path}${search}`
    }, [projectId, task?._id, taskRouteView, taskListKind])

    const hasParentTask = Boolean(
        task?.parentTask &&
            (Array.isArray(task.parentTask)
                ? task.parentTask.length > 0
                : Object.keys(task.parentTask || {}).length > 0)
    )
    const canAddSubtasks = !/^bug$/i.test(String(task?.type || 'Task').trim())

    const handleOpenTaskDetails = useCallback(
        (e) => {
            e?.stopPropagation?.()
            onTaskClick?.(task._id)
        },
        [onTaskClick, task._id]
    )

    const handleOpenTaskInNewTab = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (!taskDeepLink) return
            window.open(taskDeepLink, '_blank', 'noopener,noreferrer')
        },
        [taskDeepLink]
    )

    const handleCopyTaskLink = useCallback(
        async (e) => {
            e?.stopPropagation?.()
            const ok = await copyTaskContextLink(taskDeepLink)
            showActionNotification({
                type: ok ? 'success' : 'error',
                message: ok ? 'Task link copied' : 'Could not copy link',
                notificationKey: 'task-card-link-copied',
            })
        },
        [taskDeepLink]
    )

    const handleAddSubtaskFromMenu = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (hasParentTask || !canAddSubtasks || readOnlyActions) return
            onTaskClick?.(task._id, 'add-subtasks')
        },
        [hasParentTask, canAddSubtasks, readOnlyActions, onTaskClick, task._id]
    )

    const handleDeleteTaskFromMenu = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (!canDeleteModule || !task?._id) return
            confirmDeleteTask({
                type: task?.type,
                onConfirm: async () => {
                    try {
                        const result = await dispatch(
                            isMainCard
                                ? mainTaskDelete(task._id)
                                : taskDeleteSupport(task._id)
                        )
                        const data = result?.payload?.data
                        const okDelete = isMainCard
                            ? isTaskMutationSuccess(data)
                            : data?.success
                        if (okDelete) {
                            showActionNotification({
                                message:
                                    data?.message ||
                                    'Task deleted successfully',
                                subMessage: task.title,
                                notificationKey: 'task-deleted-from-board',
                            })
                            refreshTasks?.(0)
                        } else {
                            showActionNotification({
                                type: 'error',
                                message:
                                    data?.message || 'Could not delete task',
                                notificationKey: 'task-delete-failed-board',
                            })
                        }
                    } catch {
                        showActionNotification({
                            type: 'error',
                            message: 'Could not delete task',
                            notificationKey: 'task-delete-failed-board',
                        })
                    }
                },
            })
        },
        [
            canDeleteModule,
            task._id,
            task.title,
            task?.type,
            dispatch,
            isMainCard,
            refreshTasks,
        ]
    )

    return (
        <Dropdown
            trigger={['contextMenu']}
            placement="bottomLeft"
            overlayClassName={styles.taskContextDropdown}
            popupRender={() => (
                <div className={styles.taskContextMenu}>
                    {((!hasParentTask && canAddSubtasks) || !isMainCard) &&
                        !readOnlyActions && (
                        <div className={styles.taskContextMenuSection}>
                            {!isMainCard && (
                                <button
                                    type="button"
                                    className={styles.taskContextMenuItem}
                                    onClick={(ev) => {
                                        ev.stopPropagation()
                                        onToggleComplete?.()
                                    }}
                                    disabled={isTogglingComplete}
                                >
                                    <Icon
                                        icon="mdi:check-circle-outline"
                                        width={16}
                                        height={16}
                                        style={{
                                            color: '#757677',
                                            marginRight: 8,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span>
                                        {isTaskCompleted
                                            ? 'Mark incomplete'
                                            : 'Mark complete'}
                                    </span>
                                </button>
                            )}
                            {!hasParentTask && canAddSubtasks && (
                                <button
                                    type="button"
                                    className={styles.taskContextMenuItem}
                                    onClick={handleAddSubtaskFromMenu}
                                >
                                    <Icon
                                        icon="cuida:subtask-outline"
                                        width={16}
                                        height={16}
                                        style={{
                                            color: '#757677',
                                            marginRight: 8,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span>Add subtask</span>
                                </button>
                            )}
                        </div>
                    )}
                    <div className={styles.taskContextMenuSection}>
                        <button
                            type="button"
                            className={styles.taskContextMenuItem}
                            onClick={handleOpenTaskDetails}
                        >
                            <Icon
                                icon="si:eye-line"
                                width={16}
                                height={16}
                                style={{
                                    color: '#757677',
                                    marginRight: 8,
                                    flexShrink: 0,
                                }}
                            />
                            <span>Open task details</span>
                        </button>
                        <button
                            type="button"
                            className={styles.taskContextMenuItem}
                            onClick={handleOpenTaskInNewTab}
                        >
                            <Icon
                                icon="fluent:open-48-filled"
                                width={16}
                                height={16}
                                style={{
                                    color: '#757677',
                                    marginRight: 8,
                                    flexShrink: 0,
                                }}
                            />
                            <span>Open in new tab</span>
                        </button>
                        <button
                            type="button"
                            className={styles.taskContextMenuItem}
                            onClick={handleCopyTaskLink}
                        >
                            <Icon
                                icon="mdi:link-variant"
                                width={16}
                                height={16}
                                style={{
                                    color: '#757677',
                                    marginRight: 8,
                                    flexShrink: 0,
                                }}
                            />
                            <span>Copy task link</span>
                        </button>
                    </div>

                    {canDeleteModule && (
                        <div className={styles.taskContextMenuSection}>
                            <button
                                type="button"
                                className={`${styles.taskContextMenuItem} ${styles.taskContextMenuItemDanger}`}
                                onClick={handleDeleteTaskFromMenu}
                            >
                                <Icon
                                    icon="fluent:delete-20-regular"
                                    width={16}
                                    height={16}
                                    style={{
                                        color: '#dc2626',
                                        marginRight: 8,
                                        flexShrink: 0,
                                    }}
                                />
                                <span>{getDeleteTaskMenuLabel(task?.type)}</span>
                            </button>
                        </div>
                    )}
                </div>
            )}
        >
            <div onContextMenu={(e) => e.preventDefault()}>{children}</div>
        </Dropdown>
    )
}

export default TaskContextMenu
